import { Component, inject, input, model, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageService } from 'primeng/api';
import { SettingsService } from '../../settings-page/settings-service/settings.service';

@Component({
  selector: 'app-on-demand-settings-dialog',
  imports: [FormsModule, ButtonModule, DialogModule, InputNumberModule],
  templateUrl: './on-demand-settings-dialog.component.html',
})
export class OnDemandSettingsDialogComponent {
  private settingsService = inject(SettingsService);
  private messageService = inject(MessageService);

  visible = model(false);
  currencyCode = input('GBP');
  currentlyEnabled = input(false);
  initialBudget = input<number | null>(null);
  /** When true, saving enables on-demand (used when opening from the Enable button). */
  enableOnSave = input(false);

  saved = output<void>();

  saving = false;
  disabling = false;
  budget: number | null = null;

  get canSave(): boolean {
    return this.budget !== null && this.budget >= 0;
  }

  onDialogShow(): void {
    this.budget = this.initialBudget();
    this.saving = false;
    this.disabling = false;
  }

  close(): void {
    this.visible.set(false);
  }

  async save(): Promise<void> {
    if (!this.canSave || this.saving || this.disabling) {
      return;
    }

    this.saving = true;
    try {
      await this.settingsService.patchSettings({
        on_demand_enabled: this.enableOnSave() || this.currentlyEnabled(),
        on_demand_monthly_budget: this.budget!.toFixed(4),
      });
      this.messageService.add({
        severity: 'success',
        summary: 'Saved',
        detail: this.enableOnSave()
          ? 'On-demand usage enabled.'
          : 'On-demand settings updated.',
      });
      this.saved.emit();
      this.close();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Could not save on-demand settings.',
      });
    } finally {
      this.saving = false;
    }
  }

  async disable(): Promise<void> {
    if (this.saving || this.disabling) {
      return;
    }

    this.disabling = true;
    try {
      await this.settingsService.patchSettings({
        on_demand_enabled: false,
        on_demand_monthly_budget:
          this.budget !== null
            ? this.budget.toFixed(4)
            : this.initialBudget() !== null
              ? this.initialBudget()!.toFixed(4)
              : null,
      });
      this.messageService.add({
        severity: 'success',
        summary: 'Saved',
        detail: 'On-demand usage disabled.',
      });
      this.saved.emit();
      this.close();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Could not disable on-demand usage.',
      });
    } finally {
      this.disabling = false;
    }
  }
}
